import { useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { EmptyState, PageHeader, Spinner, useToast } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import { useTemplates, useUpdateRequirement } from '../hooks/useTemplates';
import { IvSubnav } from '../components/IvSubnav';
import { TierChip } from '../components/chips';
import { getErrorMessage, pluralize } from '../utils/format';
import styles from './TemplatesPage.module.css';

export function TemplatesPage() {
  const toast = useToast();
  const { templates, isLoading, isError, error, refetch } = useTemplates();
  const update = useUpdateRequirement();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // Select the first template once the list arrives (or if the selected one disappears).
  useEffect(() => {
    if (templates.length === 0) return;
    if (!selectedId || !templates.some((t) => t.id === selectedId)) {
      setSelectedId(templates[0].id);
    }
  }, [templates, selectedId]);

  const selected = templates.find((t) => t.id === selectedId) ?? null;

  function toggle(requirementId: string, required: boolean) {
    if (!selected) return;
    update.mutate(
      { templateId: selected.id, requirementId, required },
      {
        onError: (err) => toast.error(getErrorMessage(err, "Couldn't update the requirement")),
      },
    );
  }

  return (
    <div className={patterns.page}>
      <IvSubnav />
      <PageHeader
        title="Verification templates"
        subtitle='Each appointment type defines what a complete verification must contain. This is what the tiers fill toward and what "ready to review" is measured against.'
      />

      {isLoading && templates.length === 0 && (
        <div className={styles.loading}>
          <Spinner />
        </div>
      )}

      {isError && (
        <div className={patterns.panel}>
          <EmptyState
            compact
            title="Couldn't load templates"
            description={getErrorMessage(error, 'Try again in a moment.')}
            action={{ label: 'Retry', onClick: () => refetch() }}
          />
        </div>
      )}

      {!isLoading && !isError && templates.length === 0 && (
        <div className={patterns.panel}>
          <EmptyState
            title="No templates yet"
            description="Templates are created per appointment type by the PracticeNova team. Ask us to set up the first one."
          />
        </div>
      )}

      {templates.length > 0 && (
        <div className={styles.grid}>
          <div className={clsx(patterns.panel, styles.list)} role="listbox" aria-label="Templates">
            {templates.map((t) => {
              const required = t.requirements.filter((r) => r.required).length;
              return (
                <button
                  key={t.id}
                  type="button"
                  role="option"
                  aria-selected={t.id === selectedId}
                  className={clsx(styles.item, t.id === selectedId && styles.itemActive)}
                  onClick={() => setSelectedId(t.id)}
                >
                  <div className={styles.itemName}>{t.name}</div>
                  <div className={styles.itemCount}>
                    {pluralize(t.requirements.length, 'question')} · {required} required
                  </div>
                </button>
              );
            })}
          </div>

          {selected && (
            <div className={clsx(patterns.panel, styles.detail)}>
              <div className={styles.detailHeader}>
                <div>
                  <div className={styles.detailTitle}>{selected.name}</div>
                  <div className={styles.detailSub}>
                    What every verification for this appointment type must answer
                    {selected.serviceTypeCodes.length > 0 &&
                      ` · service types ${selected.serviceTypeCodes.join(', ')}`}
                  </div>
                </div>
              </div>

              {selected.requirements.length === 0 && (
                <div className={patterns.gridTableEmpty}>This template has no questions yet.</div>
              )}

              {selected.requirements.map((r) => (
                <div key={r.id} className={styles.req}>
                  <span className={styles.reqLabel}>{r.label}</span>
                  <TierChip tier={r.tier} />
                  <label className={styles.switch}>
                    <input
                      type="checkbox"
                      className={styles.switchInput}
                      checked={r.required}
                      onChange={(e) => toggle(r.id, e.target.checked)}
                      aria-label={`${r.label} — required`}
                    />
                    <span className={styles.switchTrack} aria-hidden>
                      <span className={styles.switchThumb} />
                    </span>
                    <span className={styles.switchText}>{r.required ? 'Required' : 'Optional'}</span>
                  </label>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
